import { useEffect, useRef, useState } from 'react';
import { loadHyper } from '@juspay-tech/hyper-js';
import { HyperElements, UnifiedCheckout } from '@juspay-tech/react-hyper-js';
import { CreditCard, Loader2 } from 'lucide-react';

const base = import.meta.env.VITE_API_URL || '/api';
type FormData = { status: string; clientSecret?: string; publishableKey?: string };

export function PartnerCardSetup() {
  // Fragment credentials are not sent in HTTP paths or referrers. Never put
  // the partner's server API key in this page, telemetry, or local storage.
  const token = useRef(window.location.hash.slice(1));
  const [data, setData] = useState<FormData>();
  const [hyper, setHyper] = useState<ReturnType<typeof loadHyper>>();
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const started = useRef(false);

  async function call(path: string, method = 'GET'): Promise<FormData> {
    const response = await fetch(`${base}/partner/card-setup${path}`, {
      method, headers: { Authorization: `Bearer ${token.current}` }, cache: 'no-store'
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'Unable to verify card setup. Please try again.');
    return result.data;
  }
  async function check() {
    setChecking(true); setError('');
    try {
      const result = await call('/status');
      if (result.status === 'saved') setData(result);
      else setError('Your card has not been confirmed yet. Finish the form, then check again.');
    } catch (e) { setError(e instanceof Error ? e.message : 'Unable to verify card setup.'); }
    finally { setChecking(false); }
  }
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!/^[a-f0-9]{64}$/.test(token.current)) { setError('This setup link is invalid. Request a new link from your integration administrator.'); return; }
    const meta = document.createElement('meta'); meta.name = 'referrer'; meta.content = 'no-referrer'; document.head.appendChild(meta);
    void (async () => {
      try {
        const result = await call('', 'POST');
        if (result.status === 'saved') { setData(result); return; }
        if (!result.publishableKey || !result.clientSecret) throw new Error('Card setup is temporarily unavailable.');
        setHyper(loadHyper(result.publishableKey, {
          customBackendUrl: result.publishableKey.startsWith('pk_snd') ? 'https://sandbox.hyperswitch.io' : 'https://api.hyperswitch.io'
        }));
        setData(result);
        // Redirect parameters are never accepted as proof of successful setup.
        if (window.location.search) await check();
      } catch (e) { setError(e instanceof Error ? e.message : 'Unable to open card setup.'); }
    })();
  }, []);

  const checkoutOptions = { displaySavedPaymentMethodsCheckbox: false,
    sdkHandleConfirmPayment: { handleConfirm: true, buttonText: 'Verify and save card',
      confirmParams: { return_url: `${window.location.origin}/partner-card-setup#${token.current}` } } };
  return <main className="sentry-block min-h-screen bg-gray-50 px-4 py-16">
    <section className="mx-auto max-w-lg rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
      <CreditCard className="mb-4 h-8 w-8 text-blue-600" />
      <h1 className="text-2xl font-semibold text-gray-900">Partner billing card</h1>
      {data?.status === 'saved' ? <p role="status" className="mt-4 text-green-700">Your card is saved. Future order payments will use this card. You can close this window.</p> : <>
        <p className="mt-3 text-sm text-gray-600">Save one card for your organization’s Bloom orders. Replacing it changes the card used for future payments.</p>
        <p className="mt-3 text-sm text-gray-600">A $1 verification charge will be refunded. Bloom charges order costs only when your integration requests payment.</p>
        {data?.clientSecret && data.publishableKey && hyper ? <div className="mt-6">
          <HyperElements hyper={hyper} options={{ clientSecret: data.clientSecret, publishableKey: data.publishableKey, appearance: { theme: 'default' } }}>
            <UnifiedCheckout id="partner-card-setup" options={checkoutOptions}
              onError={() => setError('Card setup could not be completed. Check your details and try again.')} />
          </HyperElements>
          <button className="mt-4 rounded-md border border-gray-300 px-4 py-2 text-sm disabled:opacity-50" onClick={() => void check()} disabled={checking}>
            {checking ? 'Checking…' : 'Check card status'}
          </button>
        </div> : !error && <Loader2 aria-label="Loading card form" className="mt-6 h-6 w-6 animate-spin" />}
      </>}
      {error && <p role="alert" className="mt-4 text-sm text-red-700">{error}</p>}
    </section>
  </main>;
}
